import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Avatar, IconButton, Tooltip, Alert, Fab, CircularProgress, Chip, Stack,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import { createTeachingChatMarkdownComponents, type PreviewState } from './teachingChatMarkdownComponents';
import type { Message } from './teachingChatUtils';
import SectionEmptyState from '../Common/SectionEmptyState';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { TEACHING_CHAT_STARTER_PROMPTS } from '../../constants/emptyStatePrompts';

const messageMarkdownBoxSx = {
  '& p': { mb: 1.25, lineHeight: 1.75 },
  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 1.5, mb: 1, fontWeight: 700 },
  '& ul, & ol': { pl: 3, my: 1.25 },
  '& li': { my: 0.5 },
  '& hr': { my: 2, borderColor: 'divider', borderWidth: '1px 0 0 0' },
  '& blockquote': { borderLeft: '4px solid', borderColor: 'divider', pl: 2, my: 1.5, fontStyle: 'italic', color: 'text.secondary' },
  '& table': { width: '100%', borderCollapse: 'collapse', my: 2, display: 'table', minWidth: '400px' },
  '& thead': { backgroundColor: 'grey.100' },
  '& th, & td': { border: '1px solid #e5e7eb', p: '8px', textAlign: 'left' },
  '& th': { fontWeight: 600 },
  '& a': { color: 'primary.main', textDecoration: 'underline' },
  '& strong': { fontWeight: 600 },
  '& em': { fontStyle: 'italic' },
  '& del': { textDecoration: 'line-through' },
  '& pre': { backgroundColor: 'grey.100', p: 1.5, borderRadius: 1, overflow: 'auto', my: 1.5, lineHeight: 1.6 },
  '& code': { backgroundColor: 'grey.100', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.9em' },
  '& dl': { my: 1.5 },
  '& dt': { fontWeight: 600, mt: 1 },
  '& dd': { ml: 3, mb: 0.5 },
  '& sup': { fontSize: '0.75em', verticalAlign: 'super' },
  '& sub': { fontSize: '0.75em', verticalAlign: 'sub' },
  '& mark': { backgroundColor: 'warning.light', px: 0.25, borderRadius: 0.25 },
} as const;

const streamMarkdownBoxSx = {
  ...messageMarkdownBoxSx,
  '& pre': { backgroundColor: 'grey.100', p: 1, borderRadius: 1, overflow: 'auto' },
};

/**
 * Shortcuts shown under the empty state so landing in chat doesn't hide the rest
 * of the platform. Deliberately subject-neutral so no filtering logic is needed.
 */
const JUMP_TO_LINKS = [
  { label: 'Create Test', path: '/tests/create' },
  { label: 'Grade Tests', path: '/tests/grade' },
  { label: 'Lesson Plan', path: '/small-group/lesson-plan' },
  { label: 'All tools →', path: '/dashboard' },
] as const;

export interface ChatMessagesProps {
  actionError: string | null;
  setActionError: (value: string | null) => void;
  messages: Message[];
  currentStream: string;
  isLoading: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  isNearBottom: boolean;
  assistantContentRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  handlePrintAssistant: (idx: number) => void;
  openDownloadMenu: (e: React.MouseEvent<HTMLElement>, idx: number) => void;
  setPreview: React.Dispatch<React.SetStateAction<PreviewState>>;
  onStarterPrompt?: (text: string) => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  actionError,
  setActionError,
  messages,
  currentStream,
  isLoading,
  scrollContainerRef,
  messagesEndRef,
  isNearBottom,
  assistantContentRefs,
  handlePrintAssistant,
  openDownloadMenu,
  setPreview,
  onStarterPrompt,
}) => {
  const navigate = useNavigate();
  const markdownComponents = useMemo(
    () => createTeachingChatMarkdownComponents(setPreview),
    [setPreview],
  );
  const streamMarkdownComponents = useMemo(
    () => createTeachingChatMarkdownComponents(setPreview, { compactPre: true }),
    [setPreview],
  );

  return (
    <Box
      ref={scrollContainerRef}
      sx={{ flex: 1, overflow: 'auto', backgroundColor: 'background.default', display: 'flex', justifyContent: 'center', minHeight: 0 }}
    >
      <Box sx={{ width: '100%', maxWidth: 880, px: { xs: 1.5, md: 3 }, py: 2, minHeight: '100%' }}>
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}
        {messages.length === 0 && !currentStream && !isLoading && (
          <>
            <SectionEmptyState
              icon={<SmartToyOutlinedIcon sx={{ fontSize: 48 }} />}
              title="Teaching Chat"
              description="Ask anything to plan lessons and get teaching ideas, or try one of these prompts."
              prompts={
                onStarterPrompt
                  ? TEACHING_CHAT_STARTER_PROMPTS.map((label) => ({
                      label,
                      onClick: () => onStarterPrompt(label),
                    }))
                  : undefined
              }
            />
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Or jump to
              </Typography>
              <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1}>
                {JUMP_TO_LINKS.map((link) => (
                  <Chip
                    key={link.path}
                    label={link.label}
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(link.path)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
        {messages.map((m, idx) => (
          <Box key={idx} sx={{ display: 'flex', mb: 2.5, alignItems: 'flex-start' }}>
            <Avatar sx={{ bgcolor: m.role === 'assistant' ? 'primary.main' : 'secondary.main', mr: 2, width: 28, height: 28, fontSize: 14 }}>
              {m.role === 'assistant' ? 'AI' : 'U'}
            </Avatar>
            <Box
              sx={{
                backgroundColor: m.role === 'assistant' ? 'background.paper' : '#f7f7f8',
                border: '1px solid',
                borderColor: 'divider',
                p: 2.25,
                borderRadius: 2,
                width: '100%',
                position: 'relative',
                pb: m.role === 'assistant' ? 5 : 2.25,
              }}
            >
              {m.role === 'assistant' && (
                <Box sx={{ position: 'absolute', right: 8, bottom: 8, display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Print this" arrow>
                    <IconButton size="small" aria-label="print" onClick={() => handlePrintAssistant(idx)}>
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download" arrow>
                    <IconButton size="small" aria-label="download" onClick={(e) => openDownloadMenu(e, idx)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <Box
                sx={messageMarkdownBoxSx}
                ref={(el: HTMLDivElement | null) => {
                  assistantContentRefs.current[idx] = el;
                }}
              >
                <MarkdownRenderer enableMath components={markdownComponents}>
                  {m.content}
                </MarkdownRenderer>
              </Box>
            </Box>
          </Box>
        ))}
        {!!currentStream && (
          <Box sx={{ display: 'flex', mb: 2.5, alignItems: 'flex-start' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 28, height: 28, fontSize: 14 }}>AI</Avatar>
            <Box
              sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                p: 2.25,
                borderRadius: 2,
                width: '100%',
                position: 'relative',
              }}
            >
              <Box sx={streamMarkdownBoxSx}>
                <MarkdownRenderer enableMath components={streamMarkdownComponents}>
                  {currentStream}
                </MarkdownRenderer>
              </Box>
              {isLoading && (
                <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={12} />
                  <Typography variant="caption" color="text.secondary">
                    Generating...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
        <div ref={messagesEndRef as React.Ref<HTMLDivElement>} />
      </Box>
      {!isNearBottom && (
        <Fab
          size="small"
          color="default"
          sx={{ position: 'sticky', left: 'calc(100% - 56px)', bottom: 16, boxShadow: 2 }}
          onClick={() => {
            const el = scrollContainerRef.current;
            if (!el) return;
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          }}
        >
          <KeyboardArrowDownIcon />
        </Fab>
      )}
    </Box>
  );
};

export default ChatMessages;
